import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';

export interface SignUpOptions {
  captchaToken?: string;
  /** Where the confirmation / magic link lands. Defaults to `/dashboard`. */
  emailRedirectTo?: string;
  /** Extra metadata stored in `auth.users.user_metadata` (GoTrue `options.data`). */
  data?: Record<string, unknown>;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
    opts?: SignUpOptions,
  ) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<{ error: Error | null }>;
  signInWithMagicLink: (email: string, captchaToken?: string) => Promise<{ error: Error | null }>;
  resendConfirmation: (email: string, captchaToken?: string) => Promise<{ error: Error | null }>;
  /** 010 · Solicita el correo de recuperación (resetPasswordForEmail) que aterriza en /reset-password. */
  requestPasswordReset: (email: string, captchaToken?: string) => Promise<{ error: Error | null }>;
  /** 010 · Fija la nueva contraseña usando la sesión de recuperación activa. */
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, opts?: SignUpOptions) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo:
          opts?.emailRedirectTo ?? `${window.location.origin}/dashboard`,
        ...(opts?.captchaToken ? { captchaToken: opts.captchaToken } : {}),
        ...(opts?.data ? { data: opts.data } : {}),
      },
    });
    return { error };
  };

  const signIn = async (email: string, password: string, captchaToken?: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: {
        ...(captchaToken ? { captchaToken } : {}),
      },
    });
    return { error };
  };

  const signInWithMagicLink = async (email: string, captchaToken?: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
        ...(captchaToken ? { captchaToken } : {}),
      },
    });
    return { error };
  };

  const resendConfirmation = async (email: string, captchaToken?: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
        ...(captchaToken ? { captchaToken } : {}),
      },
    });
    return { error };
  };

  // 010 · Recuperación de contraseña. La respuesta NO revela si el email existe (anti-enumeración):
  // la UI muestra siempre el mismo mensaje neutro. El enlace aterriza en /reset-password.
  const requestPasswordReset = async (email: string, captchaToken?: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
      ...(captchaToken ? { captchaToken } : {}),
    });
    return { error };
  };

  // 010 · Fija la nueva contraseña con la sesión de recuperación activa (evento PASSWORD_RECOVERY).
  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signUp,
        signIn,
        signInWithMagicLink,
        resendConfirmation,
        requestPasswordReset,
        updatePassword,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
